import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, Phone, MapPin, Star, Search, ExternalLink, MessageCircle, Copy, BadgeCheck } from "lucide-react";

// Admin-only: find real local providers on the web for a given city + category.
export const ProviderSearchDialog = ({ defaultCity = "", defaultCategory = "", jobMessage = "", contextId = "", clientName = "", clientPhone = "", clientEmail = "", onStatusChange = () => {}, triggerLabel = "", testId = "provider-search" }) => {
  const { lang } = useLang();
  const L = (es, en) => (lang === "es" ? es : en);
  const [open, setOpen] = useState(false);
  const [assigning, setAssigning] = useState("");
  const [city, setCity] = useState(defaultCity);
  const [category, setCategory] = useState(defaultCategory);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [providers, setProviders] = useState(null);
  const [statuses, setStatuses] = useState({});

  const loadStatuses = () => {
    if (!contextId) return;
    api.get("/admin/provider-status", { params: { context_id: contextId } }).then((r) => setStatuses(r.data.statuses || {})).catch(() => {});
  };

  const updateStatus = async (p, st) => {
    const key = (p.name || "").trim().toLowerCase();
    const next = statuses[key] === st ? "" : st;
    setStatuses((prev) => ({ ...prev, [key]: next }));
    try {
      await api.post("/admin/provider-status", { context_id: contextId, provider_key: key, provider_name: p.name, status: next });
      onStatusChange();
      if (next === "contratado") autoContact(p);
    } catch { toast.error(L("No se pudo guardar el estado", "Could not save status")); }
  };

  const assignLead = async (p) => {
    if (!p.business_id) return;
    setAssigning(p.name);
    try {
      await api.post("/admin/assign-lead", { business_id: p.business_id, message: jobMessage || "", city: city || "", interest: category || "", client_name: clientName || "", client_phone: clientPhone || "", client_email: clientEmail || "" });
      toast.success(L(`Solicitud enviada a ${p.name} en URBIS`, `Request sent to ${p.name} on URBIS`));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || L("No se pudo asignar", "Could not assign"));
    } finally { setAssigning(""); }
  };

  const autoContact = (p) => {
    const wa = waLink(p);
    if (wa) { window.open(wa, "_blank"); toast.success(L("Abriendo WhatsApp con el mensaje del trabajo…", "Opening WhatsApp with the job message…")); return; }
    if (p.website) { window.open(p.website, "_blank"); toast.success(L("Abriendo el sitio del proveedor para contactarlo…", "Opening the provider's website to contact them…")); return; }
    copyMsg(p); toast.info(L("Mensaje copiado (el proveedor no tiene teléfono ni web).", "Message copied (provider has no phone or website)."));
  };

  const run = async () => {
    if (!city.trim() || !category.trim()) { setError(L("Indica ciudad y categoría", "Enter city and service")); return; }
    setLoading(true); setError(""); setProviders(null);
    try {
      const r = await api.get("/admin/find-providers", { params: { city, category } });
      setProviders(r.data.providers || []);
      loadStatuses();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || L("Error en la búsqueda", "Search error"));
    } finally { setLoading(false); }
  };

  const buildMessage = (p) => {
    const svc = category.trim() || "a service";
    const loc = city.trim() ? ` in ${city.trim()}` : "";
    let m = `Hi ${p.name}, this is URBIS Services (https://urbis.shop). We have a client${loc} who needs ${svc}.`;
    if (jobMessage && jobMessage.trim()) m += ` Client details: "${jobMessage.trim()}".`;
    m += " Do you have availability and could you send me a quote? Thanks!";
    m += " P.S. We'd love to have your business on URBIS — join our directory of trusted local pros and get more customers at https://urbis.shop";
    return m;
  };

  const waLink = (p) => {
    const digits = (p.phone || "").replace(/\D/g, "");
    if (digits.length < 10) return null;
    const num = digits.length === 10 ? `1${digits}` : digits;
    return `https://wa.me/${num}?text=${encodeURIComponent(buildMessage(p))}`;
  };

  const copyMsg = async (p) => {
    try { await navigator.clipboard.writeText(buildMessage(p)); toast.success(L("Mensaje copiado", "Message copied")); }
    catch { toast.error(L("No se pudo copiar", "Could not copy")); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-sm border-primary/40 text-primary hover:bg-primary/5" onClick={() => { setOpen(true); setCity(defaultCity); setCategory(defaultCategory); loadStatuses(); }} data-testid={`${testId}-btn`}>
        <Search className="mr-1.5 h-3.5 w-3.5" /> {triggerLabel || L("Buscar proveedores web", "Find web providers")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-primary" /> {L("Proveedores en la web", "Providers on the web")}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1"><label className="text-xs font-bold uppercase text-slate-500">{L("Ciudad", "City")}</label><Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={L("Ej. Dallas", "e.g. Dallas")} className="mt-1 rounded-sm" data-testid={`${testId}-city`} /></div>
            <div className="flex-1"><label className="text-xs font-bold uppercase text-slate-500">{L("Servicio", "Service")}</label><Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder={L("Ej. plomería", "e.g. plumbing")} className="mt-1 rounded-sm" data-testid={`${testId}-category`} /></div>
            <Button onClick={run} disabled={loading} className="rounded-sm bg-primary hover:bg-orange-600" data-testid={`${testId}-run`}>{loading ? L("Buscando…", "Searching…") : L("Buscar", "Search")}</Button>
          </div>
          {error && <p className="rounded-sm bg-red-50 px-3 py-2 text-sm text-red-600" data-testid={`${testId}-error`}>{error}</p>}
          <div className="space-y-2">
            {providers && providers.length === 0 && <p className="py-8 text-center text-sm text-slate-400">{L("Sin resultados.", "No results.")}</p>}
            {(providers || []).map((p, i) => (
              <div key={p.name || i} className={`rounded-sm border bg-white p-3 ${p.in_urbis ? "border-accent ring-1 ring-accent/30" : "border-slate-200"}`} data-testid={`${testId}-result-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-head font-bold text-secondary">{p.name}</p>
                    {p.in_urbis && (p.business_id ? (
                      <a href={`/business/${p.business_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent hover:bg-accent/25" data-testid={`${testId}-recommended-${i}`}><BadgeCheck className="h-3 w-3" /> Recomendado · en URBIS</a>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent" data-testid={`${testId}-recommended-${i}`}><BadgeCheck className="h-3 w-3" /> Recomendado · en URBIS</span>
                    ))}
                  </div>
                  {p.rating != null && <span className="flex shrink-0 items-center gap-1 rounded-sm bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary"><Star className="h-3 w-3 fill-primary" />{p.rating} {p.reviews ? `(${p.reviews})` : ""}</span>}
                </div>
                <div className="mt-1 space-y-0.5 text-sm text-slate-600">
                  {p.address && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{p.address}</p>}
                  {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-1.5 text-primary hover:underline"><Phone className="h-3.5 w-3.5" />{p.phone}</a>}
                  {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" />{L("Sitio web", "Website")}</a>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-100 pt-2">
                  {p.in_urbis && p.business_id && (
                    <a href={`/business/${p.business_id}`} target="_blank" rel="noreferrer" data-testid={`${testId}-assign-${i}`}
                       className="inline-flex items-center gap-1.5 rounded-sm bg-accent px-3 py-1.5 text-xs font-bold text-white hover:opacity-90">
                      <BadgeCheck className="h-3.5 w-3.5" /> Ver en URBIS y asignar
                    </a>
                  )}
                  {waLink(p) && (
                    <a href={waLink(p)} target="_blank" rel="noreferrer" data-testid={`${testId}-wa-${i}`}
                       className="inline-flex items-center gap-1.5 rounded-sm bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white hover:opacity-90">
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                  )}
                  {p.website && (
                    <a href={p.website} target="_blank" rel="noreferrer" data-testid={`${testId}-web-${i}`}
                       className="inline-flex items-center gap-1.5 rounded-sm bg-secondary px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-700">
                      <Globe className="h-3.5 w-3.5" /> {L("Contactar por web", "Contact via website")}
                    </a>
                  )}
                  <button type="button" onClick={() => copyMsg(p)} data-testid={`${testId}-copy-${i}`}
                          className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-primary hover:text-primary">
                    <Copy className="h-3.5 w-3.5" /> {L("Copiar mensaje", "Copy message")}
                  </button>
                </div>
                {contextId && (() => {
                  const key = (p.name || "").trim().toLowerCase();
                  const cur = statuses[key] || "";
                  const opts = [["contactado", L("Contactado", "Contacted")], ["contratado", L("Contratado", "Hired")], ["descartado", L("Descartado", "Dismissed")]];
                  const cls = (v) => cur === v ? (v === "contratado" ? "bg-accent text-white" : v === "descartado" ? "bg-slate-400 text-white" : "bg-primary text-white") : "border border-slate-200 text-slate-500 hover:border-slate-300";
                  return (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`${testId}-status-row-${i}`}>
                      <span className="text-xs font-semibold text-slate-400">{L("Estado:", "Status:")}</span>
                      {opts.map(([val, label]) => (
                        <button key={val} type="button" onClick={() => updateStatus(p, val)} data-testid={`${testId}-status-${i}-${val}`}
                          className={`rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${cls(val)}`}>{label}</button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
